import React, { useState, useEffect, useCallback } from "react";
import ApiCall from "../../utils/apicall";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  People as PeopleIcon,
} from "@mui/icons-material";

import SmartTable from "../../components/SmartTable/SmartTable";
// ─── Constants ───────────────────────────────────────────────────────────────

const ALL_MODULES = [
  { slug: "weldassure",          label: "WeldAssure" },
  { slug: "sfm_inspection_plus", label: "SFM Inspection Plus" },
  { slug: "sfm_live_operations", label: "SFM Operations" },
  { slug: "coreassure",           label: "CoreAssure" },
];

const PLAN_OPTIONS = ["core", "custom", "full"];

const SUB_STATUS_OPTIONS = [
  "none", "trialing", "active", "past_due", "canceled", "unpaid",
];

const MODULE_COLORS = {
  weldassure:          "primary",
  sfm_inspection_plus: "secondary",
  sfm_live_operations: "success",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Uses centralized ApiCall (auth token injected automatically)
const api = {
  get:   (path, cfg) => ApiCall.get(`/admin/tenants${path}`, cfg),
  post:  (path, data) => ApiCall.post(`/admin/tenants${path}`, data),
  put:   (path, data) => ApiCall.put(`/admin/tenants${path}`, data),
  patch: (path, data) => ApiCall.patch(`/admin/tenants${path}`, data),
};

const planColor = (plan) =>
  plan === "full" ? "success" : plan === "custom" ? "primary" : "default";

const statusColor = (s) =>
  s === "active"   ? "success" :
  s === "trialing" ? "info"    :
  s === "past_due" ? "warning" :
  s === "canceled" ? "error"   : "default";

const EMPTY_FORM = {
  name:               "",
  slug:               "",
  plan:               "custom",
  activeModules:      [],
  adminEmail:         "",
  subscriptionStatus: "none",
  trialEndsAt:        "",
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function TenantsAdmin() {
  const [tenants, setTenants]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search,  setSearch]    = useState("");
  const [dialog,  setDialog]    = useState(null);   // null | "create" | "edit"
  const [editing, setEditing]   = useState(null);   // tenant object
  const [form,    setForm]       = useState(EMPTY_FORM);
  const [saving,  setSaving]    = useState(false);
  const [error,   setError]     = useState("");

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const { data } = await api.get("/", { params });
      setTenants(data.data?.tenants || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);

  // ── Dialog helpers ────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError("");
    setDialog("create");
  };

  const openEdit = (tenant) => {
    setEditing(tenant);
    setForm({
      name:               tenant.name,
      slug:               tenant.slug,
      plan:               tenant.plan,
      activeModules:      tenant.activeModules || [],
      adminEmail:         tenant.adminEmail || "",
      subscriptionStatus: tenant.subscriptionStatus || "none",
      trialEndsAt:        tenant.trialEndsAt
                            ? tenant.trialEndsAt.slice(0, 10)
                            : "",
    });
    setError("");
    setDialog("edit");
  };

  const closeDialog = () => { setDialog(null); setEditing(null); setError(""); };

  // ── Form field helpers ────────────────────────────────────────────────────

  const handleField = (field) => (e) => {
    const val = e.target.value;
    setForm((f) => {
      const next = { ...f, [field]: val };
      // Auto-sync plan when modules change
      if (field === "plan") {
        if (val === "full")  next.activeModules = ALL_MODULES.map((m) => m.slug);
        if (val === "core")  next.activeModules = [];
      }
      return next;
    });
  };

  const toggleModule = (slug) => {
    setForm((f) => {
      const has = f.activeModules.includes(slug);
      const mods = has
        ? f.activeModules.filter((s) => s !== slug)
        : [...f.activeModules, slug];
      const plan = mods.length === 0 ? "core"
                 : mods.length === ALL_MODULES.length ? "full" : "custom";
      return { ...f, activeModules: mods, plan };
    });
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setError("");
    if (!form.name.trim()) { setError("Name is required"); return; }
    if (!form.slug.trim() && dialog === "create") { setError("Slug is required"); return; }

    setSaving(true);
    try {
      if (dialog === "create") {
        await api.post("/", form);
      } else {
        await api.put(`/${editing.id}`, form);
      }
      closeDialog();
      fetchTenants();
    } catch (e) {
      setError(e.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle active ─────────────────────────────────────────────────────────

  const handleToggleActive = async (tenant) => {
    try {
      await api.patch(`/${tenant.id}/toggle-active`);
      fetchTenants();
    } catch (e) {
      console.error(e);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const filtered = tenants.filter((t) =>
    !search ||
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.slug.toLowerCase().includes(search.toLowerCase()) ||
    (t.adminEmail || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Tenant Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Provision shops, assign subscription modules, and manage account status.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchTenants} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New Tenant
          </Button>
        </Stack>
      </Stack>

      {/* Search */}
      <Card sx={{ mb: 2, p: 2 }}>
        <TextField
          size="small"
          placeholder="Search by name, slug, or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 360 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Card>

      {/* Table */}
      <Card>
        <TableContainer>
          <SmartTable tableId="tenants" lockedColumns={["ID", "Name / Slug", "Active"]} size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "grey.50" } }}>
                <TableCell>ID</TableCell>
                <TableCell>Name / Slug</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Modules</TableCell>
                <TableCell>Subscription</TableCell>
                <TableCell>Admin Email</TableCell>
                <TableCell align="center">Users</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No tenants found.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((t) => (
                  <TableRow
                    key={t.id}
                    hover
                    sx={{ opacity: t.isActive ? 1 : 0.5 }}
                  >
                    <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{t.id}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{t.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{t.slug}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t.plan}
                        size="small"
                        color={planColor(t.plan)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {(t.activeModules || []).map((m) => {
                          const mod = ALL_MODULES.find((x) => x.slug === m);
                          return (
                            <Chip
                              key={m}
                              label={mod?.label || m}
                              size="small"
                              color={MODULE_COLORS[m] || "default"}
                              sx={{ mb: 0.5 }}
                            />
                          );
                        })}
                        {(!t.activeModules || t.activeModules.length === 0) && (
                          <Typography variant="caption" color="text.secondary">Core only</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t.subscriptionStatus}
                        size="small"
                        color={statusColor(t.subscriptionStatus)}
                      />
                      {t.trialEndsAt && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Trial ends {new Date(t.trialEndsAt).toLocaleDateString()}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{t.adminEmail || "—"}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                        <PeopleIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                        <Typography variant="body2">{t.userCount ?? 0}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t.isActive ? "Deactivate tenant" : "Activate tenant"}>
                        <Switch
                          size="small"
                          checked={Boolean(t.isActive)}
                          onChange={() => handleToggleActive(t)}
                          disabled={t.id === 1}
                          color="success"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(t)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </SmartTable>
        </TableContainer>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={!!dialog} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog === "create" ? "New Tenant" : `Edit: ${editing?.name}`}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            {error && (
              <Typography color="error" variant="body2">{error}</Typography>
            )}

            <TextField
              label="Shop / Company Name"
              value={form.name}
              onChange={handleField("name")}
              required
              fullWidth
              size="small"
            />

            <TextField
              label="Slug (URL-safe identifier)"
              value={form.slug}
              onChange={handleField("slug")}
              required={dialog === "create"}
              disabled={dialog === "edit"}
              fullWidth
              size="small"
              helperText={dialog === "create" ? "e.g. acme-steel — cannot be changed later" : "Slug is immutable"}
            />

            <TextField
              label="Admin Email"
              value={form.adminEmail}
              onChange={handleField("adminEmail")}
              fullWidth
              size="small"
              type="email"
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  select
                  label="Plan"
                  value={form.plan}
                  onChange={handleField("plan")}
                  fullWidth
                  size="small"
                >
                  {PLAN_OPTIONS.map((p) => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  label="Subscription Status"
                  value={form.subscriptionStatus}
                  onChange={handleField("subscriptionStatus")}
                  fullWidth
                  size="small"
                >
                  {SUB_STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <TextField
              label="Trial Ends At"
              type="date"
              value={form.trialEndsAt}
              onChange={handleField("trialEndsAt")}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                Subscription Modules
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {ALL_MODULES.map((m) => {
                  const active = form.activeModules.includes(m.slug);
                  return (
                    <Chip
                      key={m.slug}
                      label={m.label}
                      onClick={() => toggleModule(m.slug)}
                      color={active ? MODULE_COLORS[m.slug] : "default"}
                      variant={active ? "filled" : "outlined"}
                      sx={{ cursor: "pointer", mb: 0.5 }}
                    />
                  );
                })}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Click to toggle. Plan auto-adjusts.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? "Saving…" : dialog === "create" ? "Create Tenant" : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
